import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, CheckCircle2, Lock, ArrowRight, Clock, X, Send, Info } from "lucide-react";

const fmt = (d) => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function Booking() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [rooms, setRooms] = useState([]);
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);

  const [roomId, setRoomId] = useState(null);
  const [pkgId, setPkgId] = useState(null);
  const [date, setDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [serviceIds, setServiceIds] = useState([]);
  const [notes, setNotes] = useState("");
  const [avail, setAvail] = useState({ slots: [], taken: [] });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [showContact, setShowContact] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/rooms"), api.get("/packages")])
      .then(([r, p]) => {
        setRooms(r.data); setPackages(p.data);
        if (r.data.length) setRoomId(r.data[0].room_id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!roomId) return;
    api.get(`/services?room_id=${roomId}`)
      .then((r) => { setServices(r.data); setServiceIds([]); })
      .catch(() => {});
  }, [roomId]);

  useEffect(() => {
    if (roomId && date) {
      api.get(`/availability?room_id=${roomId}&date=${fmt(date)}`)
        .then((r) => setAvail(r.data)).catch(() => {});
      setStartTime(null);
    }
  }, [roomId, date]);

  const room = rooms.find((r) => r.room_id === roomId);
  const pkg = packages.find((p) => p.package_id === pkgId);

  const estimate = () => {
    if (!pkg) return 0;
    let perHour = 0, flat = 0;
    serviceIds.forEach((id) => {
      const s = services.find((x) => x.service_id === id);
      if (!s) return;
      if (s.per_hour) perHour += s.price * pkg.hours;
      else flat += s.price;
    });
    return Math.round((perHour * (1 - pkg.discount) + flat) * 100) / 100;
  };

  const toggleService = (id) =>
    setServiceIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const submit = async () => {
    if (!user) return navigate("/login");
    if (!roomId || !pkgId || !date || !startTime) {
      toast.error("Please complete room, package, date and time.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        room_id: roomId, date: fmt(date), start_time: startTime,
        package_id: pkgId, service_ids: serviceIds, notes,
      });
      setConfirmed(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed. Try another slot.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="grain min-h-screen relative">
        <Navbar />
        <div className="px-6 md:px-12 lg:px-20 py-24 relative z-10 max-w-2xl mx-auto text-center" data-testid="booking-confirmation">
          <CheckCircle2 className="h-16 w-16 text-[#D90429] mx-auto" strokeWidth={1.5} />
          <h1 className="font-display text-4xl font-extrabold tracking-tight mt-6">Reservation received</h1>
          <p className="text-[#A0A0A0] mt-3">Your session is <span className="text-white font-semibold">pending confirmation</span>. Our team will lock it in shortly. Payment is collected at the studio.</p>
          <div className="border border-white/10 bg-[#121212] p-8 mt-10 text-left space-y-3">
            <Row k="Room" v={confirmed.room_name} />
            {confirmed.engineer && <Row k="Engineer" v={confirmed.engineer} />}
            <Row k="Date" v={confirmed.date} />
            <Row k="Start" v={`${confirmed.start_time} · ${confirmed.hours}h (${confirmed.package_name})`} />
            {confirmed.service_names.length > 0 && <Row k="Add-ons" v={confirmed.service_names.join(", ")} />}
            <Row k="Estimated total" v={`RON ${confirmed.total_price}`} highlight />
          </div>
          <button onClick={() => navigate("/dashboard")} data-testid="view-bookings-button"
            className="mt-8 bg-[#D90429] hover:bg-[#ED2939] text-white px-8 py-4 font-bold uppercase tracking-wide transition-all inline-flex items-center gap-2">
            View My Bookings <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="grain min-h-screen relative">
      <Navbar />
      <section className="px-6 md:px-12 lg:px-20 pt-16 pb-10 relative z-10">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D90429] mb-3">Reserve</p>
        <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter">Build your session.</h1>
      </section>

      <div className="px-6 md:px-12 lg:px-20 pb-24 relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-12">
          {/* Room */}
          <Step n="01" title="Choose your room">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {rooms.map((r) => (
                <button key={r.room_id} onClick={() => setRoomId(r.room_id)} data-testid={`select-room-${r.room_id}`}
                  className={`text-left border p-5 transition-all ${roomId === r.room_id ? "border-[#D90429] bg-[#1A0306]" : "border-white/10 hover:border-white/30"}`}>
                  <div className="aspect-video overflow-hidden mb-3">
                    <img src={r.image} alt={r.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="font-display font-bold">{r.name}</p>
                    <p className="text-sm font-bold text-[#D90429]">RON {r.price_per_hour}/hr</p>
                  </div>
                  <p className="text-xs text-[#A0A0A0] mt-1">Engineer: {r.engineer} · included</p>
                </button>
              ))}
            </div>
            {room && (
              <p className="text-xs text-[#A0A0A0] mt-4" data-testid="room-engineer-note">
                Engineer <span className="text-white font-semibold">{room.engineer}</span> is included with {room.name}.
                Choose your services below — fees are specific to this studio.
              </p>
            )}
          </Step>

          {/* Package */}
          <Step n="02" title="Pick a package">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {packages.map((p) => (
                <button key={p.package_id} onClick={() => setPkgId(p.package_id)} data-testid={`select-package-${p.package_id}`}
                  className={`text-left border p-6 transition-all ${pkgId === p.package_id ? "border-[#D90429] bg-[#1A0306]" : "border-white/10 hover:border-white/30"}`}>
                  <p className="font-display text-xl font-bold">{p.name}</p>
                  <p className="text-sm text-[#A0A0A0] mt-1">{p.hours}h{p.discount > 0 ? ` · ${p.discount * 100}% off` : ""}</p>
                  <p className="text-xs text-[#A0A0A0] mt-3">{p.description}</p>
                </button>
              ))}
            </div>
          </Step>

          {/* Date + time */}
          <Step n="03" title="Date & start time">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="border border-white/10 bg-[#121212] p-3 w-fit">
                <Calendar mode="single" selected={date} onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  data-testid="booking-calendar" />
              </div>
              <div className="flex-1">
                {!roomId || !date ? (
                  <p className="text-sm text-[#A0A0A0]">Select a room and date to see available start times.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {avail.slots.map((slot) => {
                      const taken = avail.taken.includes(slot);
                      const fits = pkg ? avail.slots.indexOf(slot) + pkg.hours <= avail.slots.length : true;
                      const disabled = taken || !fits;
                      return (
                        <button key={slot} disabled={disabled} onClick={() => setStartTime(slot)}
                          data-testid={`slot-${slot}`}
                          className={`py-2 text-sm font-medium border transition-all ${
                            startTime === slot ? "border-[#D90429] bg-[#D90429] text-white"
                            : disabled ? "border-white/5 text-white/20 cursor-not-allowed"
                            : "border-white/10 text-white/80 hover:border-[#D90429]"}`}>
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="mt-6 border-t border-white/10 pt-5">
                  <div data-testid="calendar-update-notice" className="mb-4 flex items-start gap-2.5 border border-[#D90429]/30 bg-[#D90429]/5 px-4 py-3">
                    <Info className="h-4 w-4 text-[#D90429] shrink-0 mt-0.5" />
                    <p className="text-xs text-[#A0A0A0] leading-relaxed">
                      Please note we're currently syncing the calendar with real-time studio sessions, so your requested slot may occasionally be rescheduled to a nearby time. We'll always confirm the final time with you personally.
                    </p>
                  </div>
                  <p className="text-sm text-[#A0A0A0]">Studio hours are 10:00–18:00 daily. Need time outside these hours?</p>
                  <button type="button" onClick={() => setShowContact(true)} data-testid="contact-availability-button"
                    className="mt-3 inline-flex items-center gap-2 border border-white/20 hover:border-[#D90429] hover:text-[#D90429] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wide transition-all">
                    <Clock className="h-4 w-4" /> Contact admin for more availability hours
                  </button>
                </div>
              </div>
            </div>
          </Step>

          {/* Services */}
          <Step n="04" title="Add services">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {services.map((s) => {
                const active = serviceIds.includes(s.service_id);
                return (
                  <button key={s.service_id} onClick={() => toggleService(s.service_id)} data-testid={`select-service-${s.service_id}`}
                    className={`text-left border p-5 transition-all flex items-start gap-3 ${active ? "border-[#D90429] bg-[#1A0306]" : "border-white/10 hover:border-white/30"}`}>
                    <div className={`mt-1 h-5 w-5 flex items-center justify-center border ${active ? "bg-[#D90429] border-[#D90429]" : "border-white/30"}`}>
                      {active && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <div>
                      <p className="font-semibold">{s.name}{" "}
                        {s.tbd ? <span className="text-[#A0A0A0]">Price TBD</span>
                          : <span className="text-[#D90429]">+RON {s.price}{s.per_hour ? "/hr" : ""}</span>}
                      </p>
                      <p className="text-xs text-[#A0A0A0] mt-1">{s.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </Step>

          <Step n="05" title="Notes for the studio">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="booking-notes"
              placeholder="Tell us about your session — instruments, references, headcount..."
              className="bg-[#121212] border-white/10 focus:border-[#D90429] rounded-none min-h-[120px]" />
          </Step>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="border border-white/10 bg-[#121212] p-8 sticky top-28">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A0A0A0] mb-6">Session summary</p>
            <div className="space-y-3 text-sm">
              <Row k="Room" v={room?.name || "—"} />
              <Row k="Engineer" v={room?.engineer || "—"} />
              <Row k="Package" v={pkg ? `${pkg.name} (${pkg.hours}h)` : "—"} />
              <Row k="Date" v={date ? fmt(date) : "—"} />
              <Row k="Start" v={startTime || "—"} />
              <Row k="Add-ons" v={serviceIds.length ? `${serviceIds.length} selected` : "None"} />
            </div>
            <div className="border-t border-white/10 mt-6 pt-6">
              <div className="flex items-end justify-between">
                <span className="text-sm text-[#A0A0A0]">Estimated total</span>
                <span className="font-display text-3xl font-bold text-[#D90429]">RON {estimate()}</span>
              </div>
              <p className="text-xs text-[#A0A0A0] mt-2">Pay at the studio. No card required now.</p>
            </div>
            <button onClick={submit} disabled={submitting} data-testid="submit-booking-button"
              className="w-full mt-6 bg-[#D90429] hover:bg-[#ED2939] disabled:opacity-50 text-white py-4 font-bold uppercase tracking-wide transition-all inline-flex items-center justify-center gap-2">
              {!user ? <><Lock className="h-4 w-4" /> Sign in to book</> : submitting ? "Reserving..." : "Reserve session"}
            </button>
          </div>
        </div>
      </div>
      <ContactModal open={showContact} onClose={() => setShowContact(false)} user={user} />
      <Footer />
    </div>
  );
}

const ContactModal = ({ open, onClose, user }) => {
  const [form, setForm] = useState({ name: "", email: "", preferred_time: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [open, user]);

  const submit = async () => {
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — Ceinaru will get back to you.");
      onClose();
      setForm({ name: "", email: "", preferred_time: "", message: "" });
    } catch (e) {
      toast.error("Couldn't send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="contact-modal" onClick={onClose}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg border border-white/10 bg-[#121212] p-8">
            <div className="flex items-start justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D90429]">Request extra hours</p>
              <button onClick={onClose} data-testid="contact-close" className="text-white/60 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <h2 className="font-display text-2xl font-bold mb-1">Contact the studio</h2>
            <p className="text-sm text-[#A0A0A0] mb-6">Need studio time outside 10:00–18:00? Send Ceinaru a note and they'll arrange it.</p>
            <div className="space-y-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name"
                placeholder="Your name" className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D90429] outline-none px-3 py-3 text-sm" />
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email"
                placeholder="Your email" className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D90429] outline-none px-3 py-3 text-sm" />
              <input value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} data-testid="contact-time"
                placeholder="Preferred day & time (e.g. Fri 20:00–23:00)" className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#D90429] outline-none px-3 py-3 text-sm" />
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message"
                placeholder="Tell us what you need..." className="bg-[#0A0A0A] border-white/10 focus:border-[#D90429] rounded-none min-h-[110px]" />
            </div>
            <button onClick={submit} disabled={sending} data-testid="contact-send"
              className="w-full mt-6 bg-[#D90429] hover:bg-[#ED2939] disabled:opacity-50 text-white py-3.5 font-bold uppercase tracking-wide transition-all inline-flex items-center justify-center gap-2">
              {sending ? "Sending..." : <><Send className="h-4 w-4" /> Send request</>}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Step = ({ n, title, children }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
    <div className="flex items-center gap-4 mb-5">
      <span className="font-display text-sm text-[#D90429]">{n}</span>
      <h2 className="font-display text-xl md:text-2xl font-bold">{title}</h2>
    </div>
    {children}
  </motion.div>
);

const Row = ({ k, v, highlight }) => (
  <div className="flex items-center justify-between">
    <span className="text-[#A0A0A0]">{k}</span>
    <span className={highlight ? "text-[#D90429] font-bold" : "text-white font-medium"}>{v}</span>
  </div>
);
